import {
  useEffect,
  useState
} from "react";

import axios from "axios";

import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer,
  Legend
} from "recharts";

import jsPDF from "jspdf";

import html2canvas from "html2canvas";

export default function Bilan() {

  const [bilan, setBilan] =
    useState({
      revenus: 0,
      depenses: 0,
      benefice: 0
    });

  const [dataGraph, setDataGraph] =
    useState([]);

  const [filtre, setFiltre] =
    useState("annee");

  const [preset, setPreset] =
    useState("");

  const [dateDebut, setDateDebut] =
    useState("");

  const [dateFin, setDateFin] =
    useState("");

  const [loading, setLoading] =
    useState(false);

  const now =
    new Date();

  // =====================
  // FETCH
  // =====================

  const fetchBilan =
    async () => {

      try {

        const res =
          await axios.get(
            `/api/bilan?periode=${filtre}`
          );

        setBilan(
          res.data
        );

      } catch (err) {

        console.error(err);

      }
    };

  const fetchGraph =
    async () => {

      try {

        const res =
          await axios.get(
            "/api/bilan/journalier-complet"
          );

        setDataGraph(
          res.data || []
        );

      } catch (err) {

        console.error(err);

      }
    };

  useEffect(() => {

    fetchBilan();

    fetchGraph();

  }, [filtre]);

  // =====================
  // DATES
  // =====================

  const startOfWeek =
    new Date(now);

  startOfWeek.setDate(
    now.getDate() -
      (now.getDay() || 7) +
      1
  );

  startOfWeek.setHours(
    0,
    0,
    0,
    0
  );

  const startOfMonth =
    new Date(
      now.getFullYear(),
      now.getMonth(),
      1
    );

  const startOfYear =
    new Date(
      now.getFullYear(),
      0,
      1
    );

  // =====================
  // PRESET
  // =====================

  const setPresetRange =
    (jours) => {

      const fin =
        new Date();

      const debut =
        new Date();

      debut.setDate(
        fin.getDate() -
          jours
      );

      setDateDebut(
        debut
          .toISOString()
          .split("T")[0]
      );

      setDateFin(
        fin
          .toISOString()
          .split("T")[0]
      );

      setPreset(jours);
    };

  const resetFiltres =
    () => {

      setDateDebut("");

      setDateFin("");

      setPreset("");
    };

  // =====================
  // FILTER DATA
  // =====================

  const dataFiltre =
    dataGraph.filter(
      (d) => {

        const date =
          new Date(
            d.date
          );

        if (
          dateDebut ||
          dateFin
        ) {

          if (
            dateDebut &&
            date <
              new Date(
                dateDebut
              )
          ) {
            return false;
          }

          if (
            dateFin &&
            date >
              new Date(
                dateFin
              )
          ) {
            return false;
          }

          return true;
        }

        if (preset) {

          const diff =
            (now - date) /
            (1000 *
              60 *
              60 *
              24);

          return (
            diff <=
              preset &&
            diff >= 0
          );
        }

        if (
          filtre ===
          "jour"
        ) {

          return (
            date.toDateString() ===
            now.toDateString()
          );
        }

        if (
          filtre ===
          "semaine"
        ) {

          return (
            date >=
              startOfWeek &&
            date <= now
          );
        }

        if (
          filtre ===
          "mois"
        ) {

          return (
            date >=
              startOfMonth &&
            date <= now
          );
        }

        if (
          filtre ===
          "annee"
        ) {

          return (
            date >=
              startOfYear &&
            date <= now
          );
        }

        return true;
      }
    );

  // =====================
  // PREVIOUS DATA
  // =====================

  const getPreviousPeriodData =
    (data) => {

      return data.map(
        (d) => {

          const date =
            new Date(
              d.date
            );

          let prevDate =
            new Date(
              date
            );

          if (
            filtre ===
            "semaine"
          ) {

            prevDate.setDate(
              date.getDate() -
                7
            );

          } else if (
            filtre ===
            "mois"
          ) {

            prevDate.setMonth(
              date.getMonth() -
                1
            );

          } else if (
            filtre ===
            "annee"
          ) {

            prevDate.setFullYear(
              date.getFullYear() -
                1
            );
          }

          return {
            ...d,
            date:
              prevDate.toLocaleDateString()
          };
        }
      );
    };

  const previousData =
    getPreviousPeriodData(
      dataFiltre
    );

  const chartCompareData =
    dataFiltre.map(
      (d, i) => ({
        date:
          new Date(
            d.date
          ).toLocaleDateString(),

        revenus:
          d.revenus,

        depenses:
          d.depenses,

        benefice:
          d.benefice,

        revenus_prev:
          previousData[i]
            ?.revenus || 0,

        depenses_prev:
          previousData[i]
            ?.depenses || 0,

        benefice_prev:
          previousData[i]
            ?.benefice || 0
      })
    );

  // =====================
  // EVOLUTION
  // =====================

  const calcEvolution =
    (
      current,
      previous
    ) => {

      if (
        !previous ||
        previous === 0
      ) {
        return 0;
      }

      return (
        ((current -
          previous) /
          previous) *
        100
      );
    };

  const totalCurrent =
    dataFiltre.reduce(
      (acc, d) => {

        acc.revenus +=
          Number(
            d.revenus || 0
          );

        acc.depenses +=
          Number(
            d.depenses || 0
          );

        acc.benefice +=
          Number(
            d.benefice || 0
          );

        return acc;

      },
      {
        revenus: 0,
        depenses: 0,
        benefice: 0
      }
    );

  const totalPrev =
    previousData.reduce(
      (acc, d) => {

        acc.revenus +=
          Number(
            d.revenus || 0
          );

        acc.depenses +=
          Number(
            d.depenses || 0
          );

        acc.benefice +=
          Number(
            d.benefice || 0
          );

        return acc;

      },
      {
        revenus: 0,
        depenses: 0,
        benefice: 0
      }
    );

  const evol = {
    revenus:
      calcEvolution(
        totalCurrent.revenus,
        totalPrev.revenus
      ),

    depenses:
      calcEvolution(
        totalCurrent.depenses,
        totalPrev.depenses
      ),

    benefice:
      calcEvolution(
        totalCurrent.benefice,
        totalPrev.benefice
      )
  };

  // =====================
  // PDF
  // =====================

  const exportPDF =
    async () => {

      try {

        setLoading(true);

        const element =
          document.getElementById(
            "bilan-pdf"
          );

        const canvas =
          await html2canvas(
            element
          );

        const imgData =
          canvas.toDataURL(
            "image/png"
          );

        const pdf =
          new jsPDF(
            "p",
            "mm",
            "a4"
          );

        const imgWidth =
          210;

        const imgHeight =
          (canvas.height *
            imgWidth) /
          canvas.width;

        pdf.addImage(
          imgData,
          "PNG",
          0,
          10,
          imgWidth,
          imgHeight
        );

        pdf.save(
          "bilan-financier.pdf"
        );

      } catch (err) {

        console.error(err);

        alert(
          "Erreur export PDF"
        );

      }

      setLoading(false);
    };

  // =====================
  // RENDER
  // =====================

  return (
    <div className="p-3 md:p-6 min-h-[100dvh] bg-gray-100">

      {/* TOP */}
      <div className="flex flex-col md:flex-row justify-between md:items-center gap-3 mb-5">

        <h1 className="text-2xl md:text-3xl font-bold">
          Bilan financier
        </h1>

        <button
          onClick={
            exportPDF
          }
          disabled={loading}
          className="bg-black hover:bg-gray-800 transition text-white px-5 py-3 rounded-xl disabled:opacity-50"
        >
          {loading
            ? "Export..."
            : "Exporter PDF"}
        </button>

      </div>

      <div
        id="bilan-pdf"
        className="space-y-5"
      >

        {/* FILTRES */}
        <div className="flex flex-wrap gap-2">

          {[
            "jour",
            "semaine",
            "mois",
            "annee"
          ].map((p) => (

            <button
              key={p}
              onClick={() => {

                setFiltre(
                  p
                );

                resetFiltres();

              }}
              className={`px-4 py-2 rounded-xl text-sm transition ${
                filtre === p
                  ? "bg-blue-600 text-white"
                  : "bg-white hover:bg-gray-100"
              }`}
            >
              {p}
            </button>

          ))}

        </div>

        {/* PRESETS */}
        <div className="flex flex-wrap gap-2">

          <button
            onClick={() =>
              setPresetRange(
                7
              )
            }
            className="bg-blue-500 text-white px-4 py-2 rounded-xl"
          >
            7 jours
          </button>

          <button
            onClick={() =>
              setPresetRange(
                30
              )
            }
            className="bg-purple-500 text-white px-4 py-2 rounded-xl"
          >
            30 jours
          </button>

          <button
            onClick={
              resetFiltres
            }
            className="bg-gray-300 px-4 py-2 rounded-xl"
          >
            Reset
          </button>

        </div>

        {/* DATES */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">

          <input
            type="date"
            value={dateDebut}
            onChange={(e) => {

              setDateDebut(
                e.target.value
              );

              setPreset("");

            }}
            className="border px-4 py-3 rounded-xl outline-none focus:ring-2 focus:ring-blue-400"
          />

          <input
            type="date"
            value={dateFin}
            onChange={(e) => {

              setDateFin(
                e.target.value
              );

              setPreset("");

            }}
            className="border px-4 py-3 rounded-xl outline-none focus:ring-2 focus:ring-blue-400"
          />

        </div>

        {/* CARDS */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">

          <Card
            title="Revenus"
            value={`${bilan.revenus} $`}
            color="text-green-600"
            evolution={
              evol.revenus
            }
          />

          <Card
            title="Dépenses"
            value={`${bilan.depenses} $`}
            color="text-red-600"
            evolution={
              evol.depenses
            }
            reverse
          />

          <Card
            title="Bénéfice"
            value={`${bilan.benefice} $`}
            color="text-blue-600"
            evolution={
              evol.benefice
            }
          />

        </div>

        {/* GRAPH */}
        <div className="bg-white p-3 md:p-5 rounded-2xl shadow">

          {dataFiltre.length ===
            0 && (

            <p className="text-gray-500 mb-4">
              Aucune donnée pour cette période
            </p>

          )}

          <ResponsiveContainer
            width="100%"
            height={320}
          >

            <LineChart
              data={
                chartCompareData
              }
            >

              <CartesianGrid strokeDasharray="3 3" />

              <XAxis dataKey="date" />

              <YAxis />

              <Tooltip />

              <Legend />

              <Line
                dataKey="revenus"
                stroke="#16a34a"
                strokeWidth={3}
              />

              <Line
                dataKey="depenses"
                stroke="#dc2626"
                strokeWidth={3}
              />

              <Line
                dataKey="benefice"
                stroke="#2563eb"
                strokeWidth={3}
              />

              <Line
                dataKey="revenus_prev"
                stroke="#16a34a"
                strokeDasharray="5 5"
              />

              <Line
                dataKey="depenses_prev"
                stroke="#dc2626"
                strokeDasharray="5 5"
              />

              <Line
                dataKey="benefice_prev"
                stroke="#2563eb"
                strokeDasharray="5 5"
              />

            </LineChart>

          </ResponsiveContainer>

        </div>

      </div>

    </div>
  );
}

function Card({
  title,
  value,
  color,
  evolution,
  reverse = false
}) {

  const positive =
    reverse
      ? evolution <= 0
      : evolution >= 0;

  return (
    <div className="bg-white rounded-2xl shadow p-4">

      <p className="text-sm text-gray-500">
        {title}
      </p>

      <h2
        className={`text-2xl font-bold mt-2 ${color}`}
      >
        {value}
      </h2>

      <p
        className={`mt-2 text-sm font-semibold ${
          positive
            ? "text-green-600"
            : "text-red-600"
        }`}
      >
        {positive
          ? "↑"
          : "↓"}{" "}
        {Math.abs(
          evolution
        ).toFixed(1)}
        %
      </p>

    </div>
  );
}